import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/roles';

/**
 * Protected Route Props
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  allowedRoles?: UserRole[]; // Allow any of these roles (overrides requiredRole)
  redirectTo?: string;
}

/**
 * Protected Route Component
 * Wraps routes that require authentication
 *
 * Usage:
 * ```tsx
 * <Route path="/provider-dashboard" element={
 *   <ProtectedRoute requiredRole="provider">
 *     <ProviderDashboard />
 *   </ProtectedRoute>
 * } />
 * ```
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  allowedRoles,
  redirectTo
}) => {
  const { user, userProfile, loading, refreshProfile } = useAuth();
  const [profileTimeout, setProfileTimeout] = useState(false);

  // If user is authenticated but profile hasn't loaded after 8 seconds, retry once then proceed
  useEffect(() => {
    if (!user || userProfile || loading) {
      setProfileTimeout(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        await refreshProfile();
      } catch {
        // ignore
      }
      // After retry, if still no profile, set timeout flag to stop spinner
      setTimeout(() => setProfileTimeout(true), 3000);
    }, 5000);

    return () => clearTimeout(timer);
  }, [user, userProfile, loading, refreshProfile]);

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#1a1a1a'
      }}>
        <div style={{
          textAlign: 'center',
          color: '#fff'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #374151',
            borderTop: '4px solid #D4A017',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p style={{ color: '#d1d5db' }}>Loading...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Not authenticated - redirect to appropriate login
  if (!user) {
    const defaultRedirect = requiredRole === 'provider'
      ? '/provider-login'
      : '/client-login';

    return <Navigate to={redirectTo || defaultRedirect} replace />;
  }

  // Profile still loading — user is authenticated but profile not fetched yet
  // After timeout, proceed anyway (profile may load later via refreshProfile)
  if (user && !userProfile && !profileTimeout) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#1a1a1a'
      }}>
        <div style={{ textAlign: 'center', color: '#fff' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #374151',
            borderTop: '4px solid #D4A017',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p style={{ color: '#d1d5db' }}>Loading profile...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Check role requirement — supports multi-role (roles[]) with legacy fallback (role)
  const rolesToCheck = allowedRoles || (requiredRole ? [requiredRole] : []);
  if (rolesToCheck.length > 0) {
    const hasRole = rolesToCheck.some(r =>
      userProfile?.roles?.includes(r) || userProfile?.role === r
    );

    if (!hasRole) {
      // Wrong role - redirect to appropriate dashboard or login
      const hasProvider = userProfile?.roles?.includes('provider') || userProfile?.role === 'provider';
      const hasClient = userProfile?.roles?.includes('client') || userProfile?.role === 'client';

      if (hasProvider) {
        return <Navigate to="/provider-dashboard" replace />;
      } else if (hasClient) {
        return <Navigate to="/client-dashboard" replace />;
      } else {
        const loginPath = requiredRole === 'client' ? '/client-login' : '/provider-login';
        return <Navigate to={loginPath} replace />;
      }
    }
  }

  // Authenticated and authorized - render children
  return <>{children}</>;
};

export default ProtectedRoute;
